/** @jsx jsx */
import React, { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { Button, Card, H5, ResizeSensor } from '@blueprintjs/core';
import { useFetchFilteredData } from 'hooks/useFetchFilteredData';
import { WidgetContainer } from '../common/widgetContainer';
import { ErrorBoundary } from 'components/errorBoundary';

/**
 * A DashboardCard provides a generalized method for rendering a chart onto a
 * Dashboard. The Card is responsible for rendering a ChartContainer,
 * which in turn will instantiate and render the specific Chart.
 *
 * @param {string} type
 * @param dataConfiguration
 * @param widgetConfiguration
 * @param endpointQuery
 * @param selectedDefinitionName
 * @param style
 * @param title
 * @returns {*}
 * @constructor
 */
export const DashboardCard = ({ type, dataConfiguration, widgetConfiguration, style, title, showSettings = false, toggleSettingsDialog = null }) => {
	const [dimensions, setDimensions] = useState({ width: 100, height: 100 });

	function onResize(entries) {
		const entry = entries[0].contentRect;
		setDimensions({ width: entry.width, height: entry.height });
	}

	return (
		<Card style={style} css={cardCss}>
			<div css={headerCss}>
				<H5>{title}</H5>
				{showSettings && (
					<Button
						cy-test="show-settings-button"
						minimal
						onClick={toggleSettingsDialog}
						icon={'settings'}
						children={'Settings'}
					/>
				)}
			</div>
			
			<ErrorBoundary
				title={'Ooof!'}
				description={
					'An error occurred while fetching data for this widget. Refresh the page or adjust a parameter and try again.'
				}
				retryButtonText={'Fetch again'}
			>
				<ResizeSensor onResize={onResize}>
					<div css={containerCss}>
						<DashboardData
							type={type}
							widgetConfiguration={widgetConfiguration}
							dimensions={dimensions}
							dataConfiguration={dataConfiguration}
						/>
					</div>
				</ResizeSensor>
			</ErrorBoundary>
		</Card>
	);
};

function DashboardData({ type, widgetConfiguration, dimensions, dataConfiguration }) {
	// fetch the filtered data
	const { data, loading, error } = useFetchFilteredData(dataConfiguration);

	// trigger an error if there was a problem fetching data
	const [, setError] = useState(false);
	useEffect(() => {
		if (error) {
			setError(() => {
				throw error;
			});
		}
	}, [error]);

	return (
		<WidgetContainer
			data={data}
			type={type}
			loading={loading}
			dimensions={dimensions}
			selectedDefinitionName={dataConfiguration.selectedDefinition}
			widgetConfiguration={widgetConfiguration}
			endpointQuery={dataConfiguration.endpointQuery}
		/>
	);
}

const cardCss = css`
	overflow: hidden;
	display: flex;
	flex-direction: column;
	box-shadow: rgba(0,0,0, 0.1) 0 12px 48px 0;
	border-radius: 8px;
	// For widget view
	height: 100%;
`;

const containerCss = css`
	flex: 1 1 auto;
	overflow: hidden;
	position: relative;
`;

const headerCss = css`
	display: flex;
	justify-content: space-between;
`;